import React, { FC, PropsWithChildren } from 'react'
import { makeStyles } from 'tss-react/mui'
import withV4IndexPriority from '../../../styles/withV4IndexPriority'
import { Theme } from '@mui/material/styles'
import { Card, Grid } from '@mui/material'

import { StylesProps } from '../../../types/interface/styles.interface'
import { Typography } from '@dg-shared'
import { TestPropsType, TranslatedTextType } from '../../../types'
import { ThemeBrandPageBackgroundNameType } from 'BrandTheme/types'

export const useLDCardTwoColStyles = makeStyles<StylesProps>()((theme: Theme, props) =>
  withV4IndexPriority({
    container: {
      alignContent: 'center',
      backgroundColor: theme.brand.colors.background,
      height: '100vh',
      justifyContent: 'center',
      overflowY: 'auto',
      [theme.breakpoints.up('md')]: {
        minWidth: '750px',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        ...theme.brand.assets.pageBackgrounds[props.brandPageBackground],
      },
    },

    card: {
      display: 'flex',
      width: '100%', // 780 | 900
      height: `${props.cardHeight}px`, // 616 | 620
      [theme.breakpoints.up('md')]: {
        maxWidth: '920px',
      },
    },

    cardColLeft: {
      flex: '1 50%',
      display: 'flex',
      ...theme.brand.assets.pageBackgrounds[props.brandPageBackgroundCard],
      width: '100%',
      padding: `0 ${theme.spacing(4)}`,
      justifyContent: 'center',
    },

    cardColRight: {
      flex: '1 50%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: theme.spacing(2),
      overflowY: 'auto',
      [theme.breakpoints.up('sm')]: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(8),
        paddingRight: theme.spacing(8),
      },
    },

    logoWrapper: {
      display: 'flex',
      width: '100%',
      position: 'relative',
      ...theme.brand.assets.layout?.logoWrapper,
    },

    logoImage: {
      alignSelf: 'center',
      maxHeight: '69px',
      margin: 'auto',
      maxWidth: '100%',
      ...theme.brand.assets.images.logoMax,
    },

    textFooter: {
      textAlign: 'center',
      padding: 0,
      marginTop: theme.spacing(1),
      marginBottom: 0,
    },
  })
)

export interface LDCardTwoColProps {
  textLogoAlt: TranslatedTextType
  textFooter?: TranslatedTextType
  brandPageBackground: ThemeBrandPageBackgroundNameType
  brandPageBackgroundCard?: ThemeBrandPageBackgroundNameType
  cardHeight?: number
  imageLogo: string
  isReverse?: boolean
  cardClassName?: string
  testProps?: TestPropsType
}

const LDCardTwoCol: FC<PropsWithChildren<LDCardTwoColProps>> = ({
  textLogoAlt,
  textFooter,
  imageLogo,
  brandPageBackground,
  brandPageBackgroundCard,
  isReverse,
  cardHeight = 620,
  children,
  cardClassName,
  testProps,
}) => {
  const { classes, cx } = useLDCardTwoColStyles({
    brandPageBackgroundCard,
    brandPageBackground,
    leftColGradientReverse: isReverse,
    cardHeight,
  })

  const cardClassNameString = cx(classes.card, cardClassName)

  return (
    <Grid container direction='row' component='article' className={classes.container}>
      <Card className={cardClassNameString}>
        <section data-testid='desktop-card-left' {...testProps} className={classes.cardColLeft}>
          <div id={`page-card-left`} className={classes.logoWrapper}>
            <img
              id={`desctop-card-img`}
              className={classes.logoImage}
              src={imageLogo}
              alt={`${textLogoAlt} Logo`}
            />
          </div>
        </section>
        <section className={classes.cardColRight}>
          {children}
          {!!textFooter && (
            <Typography size='small' color='textDisclaimer' className={classes.textFooter}>
              {textFooter}
            </Typography>
          )}
        </section>
      </Card>
    </Grid>
  )
}

export default LDCardTwoCol
